import { Button, Checkbox, ListItemText, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import moment from "moment";

import HeaderBread from "../../../../../components/HeaderBread";
import { useDataLayerValue } from "../../../../../DataLayer/DataLayer";
import Layout from "../../../../../Layout";
import CustomHeaderTitle from "../../../../../components/constants/CustomHeaderTitle";
import CustomizedTable from "../../../../../components/UI/CustomizedTable";
import ReportDownloads from "../../../../../components/ReportDownloads";
import SearchField from "../../../../../components/SearchField";
import SwitchBar from "../../../../../components/Pages/SwitchBar";
import {
  getShorexSalesReport,
  getShorexBookingPackageOptions,
} from "../../../../../utils/ApiCalls/BackOffice/Shorex";
import { createReportDownload } from "../../../../../utils/ApiCalls/ReportDownloads";
import { addlog } from "../../../../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../../../../utils/Constant/LogConstant";
import { getReportFileName } from "../../../../../utils/Constant/ReportDownloadLabels";
import { useDebounce } from "../../../../../hooks";
import { checkPermission } from "../../../../../utils/Validation/checkPermission";

const { RangePicker } = DatePicker;
const PAGE_PATH = "/shore/back-office/shore-excursions/report/sales";

// excNo/name alone can repeat across different sailing dates (different
// excId) - the date disambiguates them. Kept separate from the base label so
// list rows can show it as a secondary line instead of crowding one line.
const excursionOptionDate = (exc) =>
  exc.startDate ? moment.utc(exc.startDate).format("YYYY-MM-DD") : undefined;
const excursionOptionLabel = (exc) => {
  const date = excursionOptionDate(exc);
  return `${exc.excNo}-${exc.name}${date ? ` (${date})` : ""}`;
};

const getDefaultDateRange = () =>
  JSON.stringify([moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]);

const parseDateRange = (range) => {
  if (!range) return null;
  try {
    const [start, end] = JSON.parse(range);
    if (start && end) return [dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")];
  } catch (e) {
    // unparsable - treat as no selection
  }
  return null;
};

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toLowerCase()) {
    case "booked":
      return { ...base, background: "#0ab402" };
    case "voided":
      return { ...base, background: "#616161" };
    case "failed":
      return { ...base, background: "#eb6363" };
    case "pending":
      return { ...base, background: "#f5a623" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const formatAmount = (val) => (val != null ? Number(val).toFixed(2) : "-");
const formatStatus = (status) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : "-";

const ShorexSalesReport = () => {
  const locationP = useLocation();
  const navigate = useNavigate();
  const [{ permission, userDetails }, dispatch] = useDataLayerValue();
  const [topTab, setTopTab] = useState("report");

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Report", route: "/shore/back-office/shore-excursions/report" },
    { title: "Sales Report", route: PAGE_PATH },
  ];

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(locationP.search);
    return params.has("dateRange") ? params.get("dateRange") : getDefaultDateRange();
  });
  const [excDateRange, setExcDateRange] = useState(
    () => new URLSearchParams(locationP.search).get("excDateRange") || ""
  );
  const parseIdList = (val) => (val ? val.split(",").filter(Boolean) : []);
  const [statuses, setStatuses] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("status"))
  );
  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(locationP.search).get("search") || ""
  );
  const debouncedSearch = useDebounce(search, 1000);
  const [excIds, setExcIds] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("excIds"))
  );
  const [excOptions, setExcOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const excRes = await getShorexBookingPackageOptions();
      const sortAlphaNumeric = (a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
      setExcOptions(
        (excRes?.data || [])
          .slice()
          .sort((a, b) => sortAlphaNumeric(excursionOptionLabel(a), excursionOptionLabel(b)))
      );
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (statuses.length) params.set("status", statuses.join(","));
    if (excIds.length) params.set("excIds", excIds.join(","));
    if (excDateRange) params.set("excDateRange", excDateRange);
    params.set("dateRange", dateRange);
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statuses, dateRange, excDateRange, excIds]);

  const columns = [
    {
      title: "Sl",
      key: "sl",
      width: 40,
      render: (val, rec, idx) => (page - 1) * pageSize + idx + 1,
    },
    { title: "Fidelio Business Date", dataIndex: "fidelioBusinessDate", key: "fidelioBusinessDate", width: 150 },
    { title: "Transaction ID", dataIndex: "transactionId", key: "transactionId", width: 160 },
    {
      title: "Excursion Date",
      dataIndex: "excDate",
      key: "excDate",
      width: 130,
      // excDate is a calendar date, not an instant - .utc() reads its date
      // part directly instead of letting the browser's local tz shift it.
      render: (val) => (val ? moment.utc(val).format("YYYY-MM-DD") : "-"),
    },
    { title: "Excursion Code", dataIndex: "excNumber", key: "excNumber", width: 120 },
    { title: "Excursion Name", dataIndex: "excName", key: "excName", width: 160 },
    { title: "Sailing ID", dataIndex: "sailingId", key: "sailingId", width: 110 },
    { title: "Cabin Number", dataIndex: "cabinNumber", key: "cabinNumber", width: 120 },
    { title: "Guest Name", dataIndex: "guestName", key: "guestName", width: 160 },
    { title: "Acc ID", dataIndex: "accId", key: "accId", width: 100 },
    { title: "Adult", dataIndex: "adultTicket", key: "adultTicket", width: 80 },
    { title: "Child", dataIndex: "childTicket", key: "childTicket", width: 80 },
    { title: "Infant", dataIndex: "infantTicket", key: "infantTicket", width: 80 },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 110,
      render: (val) => formatAmount(val),
    },
    {
      title: "Transaction Date",
      dataIndex: "transactionAt",
      key: "transactionAt",
      width: 150,
      render: (val) => (val ? moment(val).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "Status",
      dataIndex: "transactionStatus",
      key: "transactionStatus",
      width: 110,
      render: (val, row) => {
        const displayStatus = row.voided ? "voided" : val;
        return <span style={statusChipStyle(displayStatus)}>{formatStatus(displayStatus)}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (val, row) => (
        <button
          className="btn delete_btn"
          title="View"
          onClick={() => navigate(`/shore/back-office/shore-excursions/report/view/${row.id}`)}
        >
          <i className="ri-eye-line"></i>
        </button>
      ),
    },
  ];

  const buildFilterArgs = () => {
    const args = {};
    args.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (debouncedSearch) args.search = debouncedSearch;
    if (statuses.length) args.status = statuses.join(",");
    if (excIds.length) args.excIds = excIds.join(",");
    try {
      const [fromDate, toDate] = JSON.parse(dateRange || "[]");
      if (fromDate && toDate) {
        args.fromDate = fromDate;
        args.toDate = toDate;
      }
    } catch (e) {
      // no date range selected
    }
    try {
      const [excDateFrom, excDateTo] = JSON.parse(excDateRange || "[]");
      if (excDateFrom && excDateTo) {
        args.excDateFrom = excDateFrom;
        args.excDateTo = excDateTo;
      }
    } catch (e) {
      // no excursion date range selected
    }
    return args;
  };

  const getData = async (value) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = { page: value || page, limit: pageSize, ...buildFilterArgs() };
    let data = await getShorexSalesReport(args);
    if (data.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: data.error, type: "error" } });
    } else {
      setRows(data.data || []);
      setCount(data.totalCount || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const handleRequestDownload = async () => {
    if (!checkPermission(permission, "download_per", "Sales Reports", window?.location?.pathname)) {
      message.error("Download permission missing");
      return;
    }
    const res = await createReportDownload({
      moduleName: "shorex_sales_report",
      createdBy: userDetails.id,
      searchParams: buildFilterArgs(),
    });
    if (res?.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: res.error, type: "error" } });
    } else {
      addlog(
        LOG_ACTIONS.REQUESTED,
        LOG_TYPES.REPORT_DOWNLOADS,
        {
          title: "Shorex Sales Report download requested",
          moduleName: "shorex_sales_report",
          reportDownloadId: res?.id,
          fileName: getReportFileName("shorex_sales_report", res?.createdAt, res?.id, res?.timezone),
        },
        userDetails.id
      );
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: "Download requested - check Download Logs tab", type: "success" },
      });
    }
  };

  useEffect(() => {
    getData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statuses, dateRange, excDateRange, excIds]);

  return (
    <Layout type={"read_per"} module_data={"Sales Report"}>
      <CustomHeaderTitle title={"Shore Excursion Sales Report"} />
      <div>
        <HeaderBread navigation={navigation} title={"Sales Report"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Sales Report", value: "report" },
              { title: "Download Logs", value: "downloads" },
            ]}
            activeTab={topTab}
            setActiveTab={setTopTab}
          />

          {topTab === "downloads" ? (
            <ReportDownloads module="shorex_sales_report" />
          ) : (
          <div className="card">
            <div className="card-header two_item" style={{ alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Select
                  multiple
                  style={{ width: "200px", height: "40px" }}
                  displayEmpty
                  value={statuses}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setStatuses(newValue.includes("__ALL__") ? [] : newValue);
                    setPage(1);
                  }}
                  renderValue={(selected) =>
                    selected.length ? selected.map(formatStatus).join(", ") : "All Status"
                  }
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={statuses.length === 0} />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {["pending", "booked", "voided", "failed"].map((statusOption) => (
                    <MenuItem key={statusOption} value={statusOption}>
                      <Checkbox checked={statuses.includes(statusOption)} />
                      <ListItemText primary={formatStatus(statusOption)} />
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  multiple
                  style={{ width: "220px", height: "40px" }}
                  displayEmpty
                  value={excIds}
                  onChange={(e) => {
                    setExcIds(e.target.value);
                    setPage(1);
                  }}
                  renderValue={(selected) =>
                    selected.length
                      ? excOptions
                          .filter((exc) => selected.includes(String(exc.excId)))
                          .map((exc) => excursionOptionLabel(exc))
                          .join(", ")
                      : "Filter by Excursion"
                  }
                >
                  {excOptions.map((exc) => (
                    <MenuItem key={exc.excId} value={String(exc.excId)}>
                      <Checkbox checked={excIds.includes(String(exc.excId))} />
                      <ListItemText
                        primary={`${exc.excNo}-${exc.name}`}
                        secondary={excursionOptionDate(exc)}
                      />
                    </MenuItem>
                  ))}
                </Select>
                <RangePicker
                  placeholder={["Business Date", "Business Date"]}
                  style={{ width: "230px" }}
                  onChange={(date, dateString) => {
                    setDateRange(dateString[0] && dateString[1] ? JSON.stringify(dateString) : "");
                    setPage(1);
                  }}
                  value={parseDateRange(dateRange)}
                  format={"YYYY-MM-DD"}
                />
                <RangePicker
                  placeholder={["Excursion Date", "Excursion Date"]}
                  style={{ width: "230px" }}
                  onChange={(date, dateString) => {
                    setExcDateRange(dateString[0] && dateString[1] ? JSON.stringify(dateString) : "");
                    setPage(1);
                  }}
                  value={parseDateRange(excDateRange)}
                  format={"YYYY-MM-DD"}
                />
                <Button
                  variant="contained"
                  style={{ background: "var(--red)", fontWeight: "500" }}
                  onClick={() => {
                    setStatuses([]);
                    setDateRange(getDefaultDateRange());
                    setExcDateRange("");
                    setSearch("");
                    setExcIds([]);
                    setPage(1);
                  }}
                >
                  Reset Filter
                </Button>
                <Button
                  variant="contained"
                  style={{ background: "#1565c0", fontWeight: "500" }}
                  onClick={handleRequestDownload}
                >
                  Request Download
                </Button>
              </div>
              <SearchField
                value={search}
                SearchValue={() => setPage(1)}
                setValue={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="card-body">
              <div className="table_holder">
                <CustomizedTable
                  setRows={setRows}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  pageSize={pageSize}
                  page={page}
                  count={count}
                  columns={columns}
                  rows={rows}
                  title={"Sales Report"}
                />
              </div>
            </div>
          </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ShorexSalesReport;
